import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { SimpleProjectionAssumptions, SimpleProjectionYearResult } from '../../../../types/SimpleProjection';
import EditableCell from './EditableCell';
import { calculateCAGRToYear } from '../utils/calculateCAGR';
import { useStockCurrency } from '../../../../contexts/StockCurrencyContext';

interface SimpleProjectionsVisualizationProps {
  projections: SimpleProjectionYearResult[] | null;
  assumptions?: SimpleProjectionAssumptions;
  isLoading: boolean;
  error: Error | null;
  onCellEdit?: (yearLabel: string, field: 'revenueGrowthPct' | 'netIncomeMarginPct' | 'peRatioLow' | 'peRatioHigh', value: number | undefined) => void;
  isMobile?: boolean;
  noCard?: boolean;
}

const SimpleProjectionsVisualization: React.FC<SimpleProjectionsVisualizationProps> = ({
  projections,
  assumptions,
  isLoading,
  error,
  onCellEdit,
  isMobile = false,
  noCard = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { formatDisplayPrice, currencySymbol } = useStockCurrency();

  const formatCurrency = (value: number): string => {
    const absValue = Math.abs(value);
    if (absValue >= 1e9) return `${currencySymbol}${(value / 1e9)?.toFixed(2) ?? '0.00'}B`;
    if (absValue >= 1e6) return `${currencySymbol}${(value / 1e6)?.toFixed(2) ?? '0.00'}M`;
    if (absValue >= 1e3) return `${currencySymbol}${(value / 1e3)?.toFixed(2) ?? '0.00'}K`;
    return `${currencySymbol}${value?.toFixed(2) ?? '0.00'}`;
  };

  const formatPercent = (value: number): string => `${(value * 100)?.toFixed(1) ?? '0.0'}%`;

  const formatPercentWithSign = (value: number): string => {
    if (!Number.isFinite(value)) return '—';
    return `${(value * 100)?.toFixed(1) ?? '0.0'}%`;
  };

  const formatPE = (value: number): string => `${value?.toFixed(1) ?? '0.0'}`;
  // Null share prices indicate negative EPS — applying P/E to a loss
  // doesn't yield a meaningful price. Render an em-dash so users see a
  // gap rather than $0.00, which previously implied a worthless stock.
  const formatPrice = (value: number | null): string =>
    value === null ? '—' : formatDisplayPrice(value);

  const formatCAGR = (value: number | null): string => {
    if (value === null || isNaN(value)) return '—';
    return `${(value * 100)?.toFixed(0) ?? '0'}%`;
  };

  const cagrByYear = useMemo(() => {
    if (!projections || projections.length < 3) return {};
    const result: Record<string, { low: number | null; high: number | null }> = {};
    for (let i = 2; i < projections.length; i++) {
      const cagr = calculateCAGRToYear(projections, i);
      result[projections[i].yearLabel] = {
        low: cagr?.sharePriceLow ?? null,
        high: cagr?.sharePriceHigh ?? null,
      };
    }
    return result;
  }, [projections]);

  // When the headline net income disagrees with EPS × shares by more than
  // 25%, year 0 has a hidden gap between "what the company reports" and
  // "what reaches common shareholders" — typically NCI stakes, preferred
  // dividends, or one-time items that don't flow to common holders. The
  // displayed year-0 NI uses the shareholder figure; surface a small info
  // icon with the headline number so the user understands why the cell
  // doesn't match the figure they may have seen elsewhere.
  const historicalNiContext = useMemo(() => {
    if (!projections || projections.length === 0 || !assumptions) return null;
    const year0 = projections[0];
    if (!year0.isHistorical) return null;
    const reportedNi = assumptions.netIncome;
    const attributableNi = year0.netIncome;
    const denom = Math.max(Math.abs(reportedNi), Math.abs(attributableNi), 1);
    const relativeDivergence = Math.abs(reportedNi - attributableNi) / denom;
    if (relativeDivergence < 0.25) return null;
    return { reportedNi, attributableNi };
  }, [projections, assumptions]);

  // Subtle historical column tint — one consistent pass of green
  const historicalBg = isDark ? 'rgba(34, 197, 94, 0.05)' : 'rgba(34, 197, 94, 0.04)';

  // Border used between rows (set globally on Table, overrides MUI default)
  const rowBorderColor = isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)';

  // Stronger border for the header row bottom + label column right
  const strongBorderColor = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';

  // Section divider borders (slightly stronger than row borders)
  const sectionBorderColor = isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)';

  // Row label cell — uppercase, dim, left-column anchor
  const getLabelCellSx = (isGreen = false) => ({
    fontSize: '0.72rem',
    fontWeight: 600,
    py: 1.25,
    px: 2.5,
    whiteSpace: 'nowrap' as const,
    minWidth: 165,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    color: isGreen ? '#22c55e' : 'text.secondary',
    borderRight: `1px solid ${strongBorderColor}`,
    backgroundColor: 'transparent',
  });

  // Data cell — clean and flat
  const getDataCellSx = (isHistorical: boolean, isGreen = false, isBold = false) => ({
    fontSize: '0.875rem',
    fontWeight: isBold ? 600 : 500,
    py: 1.25,
    px: 2.5,
    whiteSpace: 'nowrap' as const,
    minWidth: 120,
    backgroundColor: isHistorical ? historicalBg : 'transparent',
    color: isGreen ? '#22c55e' : (isDark ? '#ffffff' : '#1a1a1a'),
  });

  // CAGR cell — always green, no bottom border on the last row
  const getCAGRCellSx = (value: number | null, isHistorical: boolean) => ({
    fontSize: '0.875rem',
    fontWeight: 700,
    py: 1.25,
    px: 2.5,
    whiteSpace: 'nowrap' as const,
    minWidth: 120,
    backgroundColor: isHistorical ? historicalBg : 'transparent',
    color: value === null ? 'text.disabled' : '#22c55e',
    borderBottom: 'none',
  });

  // Section header row — labelled band that separates each metric group
  const sectionHeaderCellSx = {
    fontSize: '0.7rem',
    fontWeight: 700,
    py: 1.5,
    px: 2.5,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.07em',
    color: isDark ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.7)',
    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.025)' : 'rgba(0, 0, 0, 0.025)',
    borderTop: `1px solid ${sectionBorderColor}`,
    borderBottom: `1px solid ${rowBorderColor}`,
  };

  const renderSectionHeader = (label: string, colCount: number) => (
    <TableRow>
      <TableCell colSpan={colCount + 1} sx={sectionHeaderCellSx}>
        {label}
      </TableCell>
    </TableRow>
  );

  // ─── Desktop Table ────────────────────────────────────────────────────────────

  const desktopTable = projections && (
    <TableContainer sx={{ flex: 1 }}>
      <Table
        size="small"
        sx={{
          tableLayout: 'auto',
          width: '100%',
          // Override MUI default row borders globally for consistent styling
          '& .MuiTableCell-root': {
            borderBottom: `1px solid ${rowBorderColor}`,
          },
          // Force consistent font size across all body cells, including EditableCell
          '& .MuiTableCell-body': {
            fontSize: '0.875rem',
          },
          // Override the global theme's dark header background — use transparent
          // so header cells inherit the card's background.paper surface seamlessly
          '& .MuiTableCell-head': {
            backgroundColor: 'transparent',
            color: 'inherit',
          },
          // Stronger bottom border on the header row
          '& .MuiTableHead-root .MuiTableCell-root': {
            borderBottom: `1px solid ${strongBorderColor}`,
          },
        }}
      >
        <TableHead>
          <TableRow>
            {/* Corner cell — title sits here as the column heading */}
            <TableCell
              sx={{
                borderRight: 'none',
                borderBottom: `1px solid ${strongBorderColor}`,
                backgroundColor: 'transparent',
                minWidth: 165,
                py: 1.75,
                px: 2.5,
              }}
            >
              <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.primary' }}>
                Projected Financials
              </Typography>
            </TableCell>
            {projections.map((p) => (
              <TableCell
                key={p.yearLabel}
                align="right"
                sx={{
                  py: 1.5,
                  px: 2.5,
                  whiteSpace: 'nowrap',
                  minWidth: 120,
                  backgroundColor: p.isHistorical ? historicalBg : 'transparent',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.9375rem',
                    fontWeight: 700,
                    color: p.isHistorical ? '#22c55e' : (isDark ? '#ffffff' : '#1a1a1a'),
                    lineHeight: 1,
                  }}
                >
                  {p.yearLabel}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {/* ── REVENUE ── */}
          {renderSectionHeader('Revenue', projections.length)}

          <TableRow>
            <TableCell sx={getLabelCellSx()}>Revenue</TableCell>
            {projections.map((p) => (
              <TableCell key={p.yearLabel} align="right" sx={getDataCellSx(p.isHistorical, false, true)}>
                {formatCurrency(p.revenue)}
              </TableCell>
            ))}
          </TableRow>

          <TableRow>
            <TableCell sx={getLabelCellSx()}>Rev Growth %</TableCell>
            {projections.map((p) =>
              p.isHistorical ? (
                <TableCell key={p.yearLabel} align="right" sx={getDataCellSx(p.isHistorical)}>
                  —
                </TableCell>
              ) : (
                <EditableCell
                  key={p.yearLabel}
                  value={p.revenueGrowthPct}
                  displayValue={formatPercentWithSign(p.revenueGrowthPct)}
                  isEditable={!!onCellEdit}
                  isOverridden={p.revenueGrowthOverridden ?? false}
                  onSave={(value) => onCellEdit?.(p.yearLabel, 'revenueGrowthPct', value)}
                  type="growthPercent"
                  align="right"
                />
              )
            )}
          </TableRow>

          {/* ── PROFITABILITY ── */}
          {renderSectionHeader('Profitability', projections.length)}

          <TableRow>
            <TableCell sx={getLabelCellSx()}>Net Income</TableCell>
            {projections.map((p) => (
              <TableCell key={p.yearLabel} align="right" sx={getDataCellSx(p.isHistorical, false, true)}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                  {formatCurrency(p.netIncome)}
                  {p.isHistorical && historicalNiContext && (
                    <Tooltip
                      title={
                        <Box sx={{ maxWidth: 280 }}>
                          <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                            Earnings for shareholders: {formatCurrency(historicalNiContext.attributableNi)}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mb: 0.75 }}>
                            Headline profit: {formatCurrency(historicalNiContext.reportedNi)}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', opacity: 0.75 }}>
                            Not all reported profit reaches shareholders — some can be owed to joint venture partners, preferred share dividends, or one-off events. We use the shareholder figure so projections stay realistic.
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: 13,
                          color: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                          cursor: 'help',
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
            ))}
          </TableRow>

          <TableRow>
            <TableCell sx={getLabelCellSx()}>Net Inc. Growth</TableCell>
            {projections.map((p) => (
              <TableCell key={p.yearLabel} align="right" sx={getDataCellSx(p.isHistorical)}>
                {p.isHistorical ? '—' : formatPercentWithSign(p.netIncomeGrowthPct)}
              </TableCell>
            ))}
          </TableRow>

          <TableRow>
            <TableCell sx={getLabelCellSx()}>Net Inc. Margin</TableCell>
            {projections.map((p) =>
              p.isHistorical ? (
                <TableCell key={p.yearLabel} align="right" sx={getDataCellSx(p.isHistorical)}>
                  {formatPercent(p.netIncomeMargin)}
                </TableCell>
              ) : (
                <EditableCell
                  key={p.yearLabel}
                  value={p.netIncomeMargin}
                  displayValue={formatPercent(p.netIncomeMargin)}
                  isEditable={!!onCellEdit}
                  isOverridden={p.marginOverridden ?? false}
                  onSave={(value) => onCellEdit?.(p.yearLabel, 'netIncomeMarginPct', value)}
                  type="percentage"
                  align="right"
                />
              )
            )}
          </TableRow>

          {/* ── EARNINGS PER SHARE ── */}
          {renderSectionHeader('Earnings Per Share', projections.length)}

          <TableRow>
            <TableCell sx={getLabelCellSx()}>EPS</TableCell>
            {projections.map((p) => (
              <TableCell key={p.yearLabel} align="right" sx={getDataCellSx(p.isHistorical, false, true)}>
                {formatPrice(p.eps)}
              </TableCell>
            ))}
          </TableRow>

          {/* ── VALUATION ── */}
          {renderSectionHeader('Valuation', projections.length)}

          <TableRow>
            <TableCell sx={getLabelCellSx()}>PE Low Est</TableCell>
            {projections.map((p) =>
              p.isHistorical ? (
                <TableCell key={p.yearLabel} align="right" sx={getDataCellSx(p.isHistorical)}>
                  {formatPE(p.peRatioLow)}
                </TableCell>
              ) : (
                <EditableCell
                  key={p.yearLabel}
                  value={p.peRatioLow}
                  displayValue={formatPE(p.peRatioLow)}
                  isEditable={!!onCellEdit}
                  isOverridden={p.peLowOverridden ?? false}
                  onSave={(value) => onCellEdit?.(p.yearLabel, 'peRatioLow', value)}
                  type="peRatio"
                  align="right"
                />
              )
            )}
          </TableRow>

          <TableRow>
            <TableCell sx={getLabelCellSx()}>PE High Est</TableCell>
            {projections.map((p) =>
              p.isHistorical ? (
                <TableCell key={p.yearLabel} align="right" sx={getDataCellSx(p.isHistorical)}>
                  {formatPE(p.peRatioHigh)}
                </TableCell>
              ) : (
                <EditableCell
                  key={p.yearLabel}
                  value={p.peRatioHigh}
                  displayValue={formatPE(p.peRatioHigh)}
                  isEditable={!!onCellEdit}
                  isOverridden={p.peHighOverridden ?? false}
                  onSave={(value) => onCellEdit?.(p.yearLabel, 'peRatioHigh', value)}
                  type="peRatio"
                  align="right"
                />
              )
            )}
          </TableRow>

          {/* ── SHARE PRICE TARGETS ── */}
          {renderSectionHeader('Share Price Targets', projections.length)}

          <TableRow>
            <TableCell sx={getLabelCellSx(true)}>Price Low</TableCell>
            {projections.map((p) => (
              <TableCell key={p.yearLabel} align="right" sx={getDataCellSx(p.isHistorical, true, true)}>
                {formatPrice(p.sharePriceLow)}
              </TableCell>
            ))}
          </TableRow>

          <TableRow>
            <TableCell sx={getLabelCellSx(true)}>Price High</TableCell>
            {projections.map((p) => (
              <TableCell key={p.yearLabel} align="right" sx={getDataCellSx(p.isHistorical, true, true)}>
                {formatPrice(p.sharePriceHigh)}
              </TableCell>
            ))}
          </TableRow>

          {/* ── COMPOUND GROWTH ── */}
          {renderSectionHeader('Price CAGR', projections.length)}

          <TableRow>
            <TableCell sx={{ ...getLabelCellSx(true), borderBottom: 'none' }}>
              Share Price CAGR
            </TableCell>
            {projections.map((p, index) => {
              const cagr = cagrByYear[p.yearLabel]?.low ?? null;
              return (
                <TableCell
                  key={p.yearLabel}
                  align="right"
                  sx={getCAGRCellSx(cagr, p.isHistorical)}
                >
                  {index < 2 ? '—' : formatCAGR(cagr)}
                </TableCell>
              );
            })}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );

  // ─── Mobile Cards ─────────────────────────────────────────────────────────────

  const mobileCards = projections && (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {projections.map((p, index) => {
        const cagr = cagrByYear[p.yearLabel]?.low ?? null;
        const sectionLabelSx = {
          fontSize: '0.65rem',
          fontWeight: 700,
          textTransform: 'uppercase' as const,
          letterSpacing: '0.07em',
          mb: 0.875,
        };
        const metricRowSx = {
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 0.625,
          borderBottom: `1px solid ${rowBorderColor}`,
          '&:last-child': { borderBottom: 'none' },
        };

        return (
          <Box
            key={p.yearLabel}
            sx={{
              width: '100%',
              borderRadius: 2,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${strongBorderColor}`,
              overflow: 'hidden',
            }}
          >
            {/* Year header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.25,
                backgroundColor: p.isHistorical ? historicalBg : (isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)'),
                borderBottom: `1px solid ${strongBorderColor}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    color: p.isHistorical ? '#22c55e' : (isDark ? '#ffffff' : '#1a1a1a'),
                  }}
                >
                  {p.yearLabel}
                </Typography>
                {p.isHistorical && (
                  <Typography
                    sx={{
                      fontSize: '0.625rem',
                      fontWeight: 600,
                      color: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    }}
                  >
                    Actual
                  </Typography>
                )}
              </Box>
              {index >= 2 && cagr !== null && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography
                    sx={{
                      fontSize: '0.625rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                    }}
                  >
                    CAGR
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#22c55e' }}>
                    {formatCAGR(cagr)}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Metric groups */}
            <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
              {/* Revenue */}
              <Box>
                <Typography sx={{ ...sectionLabelSx, color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)' }}>
                  Revenue
                </Typography>
                <Box sx={metricRowSx}>
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>Revenue</Typography>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>
                    {formatCurrency(p.revenue)}
                  </Typography>
                </Box>
                {!p.isHistorical && (
                  <Box sx={{ ...metricRowSx, borderBottom: 'none' }}>
                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>Rev Growth %</Typography>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.primary' }}>
                      {formatPercentWithSign(p.revenueGrowthPct)}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Profitability */}
              <Box sx={{ borderTop: `1px solid ${sectionBorderColor}`, pt: 1.5 }}>
                <Typography sx={{ ...sectionLabelSx, color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)' }}>
                  Profitability
                </Typography>
                <Box sx={metricRowSx}>
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>Net Income</Typography>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>
                      {formatCurrency(p.netIncome)}
                    </Typography>
                    {p.isHistorical && historicalNiContext && (
                      <Tooltip
                        title={
                          <Box sx={{ maxWidth: 240 }}>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                              Earnings for shareholders: {formatCurrency(historicalNiContext.attributableNi)}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.75 }}>
                              Headline profit: {formatCurrency(historicalNiContext.reportedNi)}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', opacity: 0.75 }}>
                              Not all reported profit reaches shareholders — some can be owed to joint ventures, preferred dividends, or one-off events. We use the shareholder figure for projections.
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                      >
                        <InfoOutlinedIcon
                          sx={{
                            fontSize: 13,
                            color: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                            cursor: 'help',
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                <Box sx={metricRowSx}>
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>Net Margin</Typography>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.primary' }}>
                    {formatPercent(p.netIncomeMargin)}
                  </Typography>
                </Box>
                <Box sx={{ ...metricRowSx, borderBottom: 'none' }}>
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>EPS</Typography>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>
                    {formatPrice(p.eps)}
                  </Typography>
                </Box>
              </Box>

              {/* Share Price Targets */}
              <Box sx={{ borderTop: `1px solid ${sectionBorderColor}`, pt: 1.5 }}>
                <Typography sx={{ ...sectionLabelSx, color: '#22c55e' }}>
                  Share Price Targets
                </Typography>
                <Box sx={metricRowSx}>
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>Price Low</Typography>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#22c55e' }}>
                    {formatPrice(p.sharePriceLow)}
                  </Typography>
                </Box>
                <Box sx={{ ...metricRowSx, borderBottom: !p.isHistorical ? `1px solid ${rowBorderColor}` : 'none' }}>
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>Price High</Typography>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#22c55e' }}>
                    {formatPrice(p.sharePriceHigh)}
                  </Typography>
                </Box>
                {!p.isHistorical && (
                  <Box sx={{ ...metricRowSx, borderBottom: 'none' }}>
                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>PE Range</Typography>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.primary' }}>
                      {formatPE(p.peRatioLow)} – {formatPE(p.peRatioHigh)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  // ─── Main content ─────────────────────────────────────────────────────────────

  const content = (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto' }}>
      {isLoading ? (
        <Skeleton variant="rectangular" sx={{ borderRadius: 1, flex: 1, minHeight: 300 }} />
      ) : error ? (
        <Alert severity="error">Unable to load projections. Please try again.</Alert>
      ) : !projections || projections.length === 0 ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
          <Typography variant="body1" color="text.secondary">
            No projection data available
          </Typography>
        </Box>
      ) : isMobile ? mobileCards : desktopTable}
    </Box>
  );

  if (noCard) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {content}
      </Box>
    );
  }

  return (
    <Card
      sx={{
        borderRadius: 2,
        backgroundColor: isDark ? '#212121' : '#f5f5f5',
        border: `1px solid ${strongBorderColor}`,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent
        sx={{
          p: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          '&:last-child': { paddingBottom: 0 },
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            p: isMobile ? 2 : 0,
          }}
        >
          {content}
        </Box>
      </CardContent>
    </Card>
  );
};

export default SimpleProjectionsVisualization;
